import Client, { Endpoint } from './client';
import * as AnalyticsCookie from './analytics-cookie';
import { Session } from './session';
import * as events from '../schemas/analytics';
import { trackPageViewForLocation, trackGenericPageView } from './mparticle_tracker';

/**
 * The structure of the "elog-config" meta tag content.
 */
export type ElogConfig = {
  elog: Config;
};

interface Gauge {
  name: string;
  value: number;
  tags?: string[];
  sample?: number;
}

interface Increment {
  name: string;
  tags?: string[];
  rate?: number;
}

export interface Config {
  host: string;
  timeout?: number;
  apiKey: string;
  buffer?: number;
  local?: boolean;
  enabled?: boolean;
  domain?: string;
  userContext?: events.UserContext;
}

const ERROR = 'error';
const INFO = 'info';
let ENABLED = true;

const userContext: Partial<events.UserContext> = {
  cookie_id: AnalyticsCookie.trackingID(),
};

const session = new Session(userContext);

function buildEvent(name, data = {}, jsError?, level?) {
  let eventName = name;
  let props;

  if (name.$type) {
    eventName = name.$type.name;
    props = name;
  } else {
    props = data;
  }

  const eventData = { ...props };
  delete eventData.$type;

  // Remove experiments from normal elog tracking as they are no longer used
  // The user_context itself is shared in a few places, so making this change
  // here and not in the indentify call will restore this functionality
  //
  // https://github.com/reverbdotcom/frontend/pull/5100/files
  eventData.user_context = { ...userContext, experiments: [] };

  return {
    level,
    name: eventName,
    data: eventData,
    error: jsError,
  };
}

export function configure(opts: Config) {
  if (Object.hasOwnProperty.call(opts, 'enabled')) {
    ENABLED = opts.enabled;
  }

  Client.configure({
    host: opts.host,
    timeout: opts.timeout,
    apiKey: opts.apiKey,
    buffer: opts.buffer,
    local: opts.local,
  });

  AnalyticsCookie.configure(opts);

  // Refresh tracking ID since we may not have been able to persist
  // the cookie on the default domain
  userContext.cookie_id = AnalyticsCookie.trackingID();

  if (opts.userContext?.request_id) {
    session.setRequestID(opts.userContext.request_id);
  }
}

export function identify(user: events.UserContext) {
  const normalizedUserAttributes = { ...user, experiments: transformExperiments(user) };
  Object.assign(userContext, normalizedUserAttributes);
}

function transformExperiments(user) {
  if (!user.experiments) return [];

  return user.experiments.map((exp) => {
    return (({ name, value }) => ({ name, value }))(exp);
  });
}

export function post(event, endpoint: Endpoint) {
  if (ENABLED !== true) { return; }
  Client.post([event], endpoint);
}

export type AnalyticsQueryParams = {
  utm_source?: string;
  utm_campaign?: string;
  utm_medium?: string;
  gs_partner?: string;
};

export function pageView(loc: Partial<Location> = window.location, doc: Partial<Document> = document) {
  session.touch(loc, doc);

  trackGenericPageView();
  trackPageViewForLocation(loc);
}

export function currentSession(): Session {
  return session;
}

/**
 * Logs an error to:
 * - Reverb Event API
 * - JS console via console.error
 * - Datadog Errors via auto instrumentation of console.error, if the datadog SDK is enabled (DATADOG_ERROR_TRACKING_ENABLED).
 */
export function error(name: string, data = {}, jsError: string | Error = null) {
  const event = buildEvent(name, data, jsError, ERROR);

  if (!jsError) {
    // eslint-disable-next-line no-param-reassign
    jsError = name;
  }

  if (!(jsError as Error).message) {
    // If we just have a string instead of an error, wrap in an error so we get a stack trace
    // eslint-disable-next-line no-param-reassign
    jsError = new Error(jsError as string);
  }

  // ensure that errors are reported to the console
  console.error(jsError);
  post(event, 'events');
}

export function info(name: string, data: Record<string, any> = {}) {
  const event = buildEvent(name, data, null, INFO);
  post(event, 'events');
}

export function gauge(gauge: Gauge) {
  post(gauge, 'gauges');
}

export function increment(increment: Increment) {
  post(increment, 'increment');
}

export const elog = {
  info,
  error,
};

export { events };
